import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, X, Image as ImageIcon, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, formatErr } from "../lib/api";

const CATEGORIES = [
  { id: "medical", label: "Medical" },
  { id: "education", label: "Education" },
  { id: "emergency", label: "Emergency" },
  { id: "food", label: "Food & Essentials" },
  { id: "housing", label: "Housing" },
  { id: "community", label: "Community" },
  { id: "disability", label: "Disability Support" },
  { id: "other", label: "Other" },
];

const URGENCIES = [
  { id: "low", label: "Low" },
  { id: "normal", label: "Normal" },
  { id: "high", label: "Urgent" },
  { id: "critical", label: "Critical" },
];

export default function CreateCampaign() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    story: "",
    amount_needed: 1000,
    category: "medical",
    urgency: "normal",
    location: "",
  });
  const [cover, setCover] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/files/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const handleCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setCover(url);
      toast.success("Cover image uploaded");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
    }
  };

  const handleProofs = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(uploadFile));
      setProofs((p) => [...p, ...urls]);
      toast.success(`${urls.length} proof file(s) uploaded`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.length < 8) return toast.error("Title must be at least 8 characters");
    if (form.story.length < 40) return toast.error("Tell your story in at least 40 characters");
    if (!form.location.trim()) return toast.error("Please add your location");
    if (form.amount_needed < 1) return toast.error("Amount must be positive");

    setSubmitting(true);
    try {
      await api.post("/campaigns", {
        ...form,
        amount_needed: parseFloat(form.amount_needed),
        cover_image_url: cover,
        proof_urls: proofs,
      });
      toast.success("Campaign submitted! Awaiting admin review.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-16 sm:py-20">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="eyebrow inline-flex items-center gap-2"><Sparkles size={12} /> Start a campaign</span>
        <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 leading-tight">
          Tell your story. <span className="italic text-brand-primary">Inspire support.</span>
        </h1>
        <p className="text-brand-muted mt-4">All campaigns are manually reviewed for trust and transparency before going live.</p>
      </motion.div>

      <form onSubmit={submit} className="mt-12 space-y-7">
        <div className="premium-card p-7 space-y-5">
          <div>
            <label className="eyebrow block mb-2">Campaign title</label>
            <input value={form.title} onChange={(e) => setField("title", e.target.value)}
                   className="input-premium" placeholder="A short, hopeful headline" data-testid="campaign-title-input" />
          </div>

          <div>
            <label className="eyebrow block mb-2">Your story</label>
            <textarea value={form.story} onChange={(e) => setField("story", e.target.value)}
                      className="input-premium" rows={7}
                      placeholder="Who needs support, why now, and how the funds will be used."
                      data-testid="campaign-story-input" />
            <p className="text-xs text-brand-muted mt-2">Minimum 40 characters. Be authentic — donors connect with honesty.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="eyebrow block mb-2">Goal (£)</label>
              <input type="number" min="1" value={form.amount_needed}
                     onChange={(e) => setField("amount_needed", e.target.value)}
                     className="input-premium" data-testid="campaign-amount-input" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Category</label>
              <select value={form.category} onChange={(e) => setField("category", e.target.value)}
                      className="input-premium" data-testid="campaign-category-select">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow block mb-2">Urgency</label>
              <select value={form.urgency} onChange={(e) => setField("urgency", e.target.value)}
                      className="input-premium" data-testid="campaign-urgency-select">
                {URGENCIES.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-2">Location</label>
            <input value={form.location} onChange={(e) => setField("location", e.target.value)}
                   className="input-premium" placeholder="City, Country (e.g. London, UK)"
                   data-testid="campaign-location-input" />
          </div>
        </div>

        <div className="premium-card p-7">
          <p className="eyebrow mb-4">Cover image</p>
          {cover ? (
            <div className="relative rounded-2xl overflow-hidden">
              <img src={cover} alt="Cover" className="w-full h-64 object-cover" />
              <button type="button" onClick={() => setCover(null)}
                      className="absolute top-3 right-3 bg-white/90 hover:bg-white rounded-full p-2"
                      data-testid="remove-cover-btn">
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="block border-2 border-dashed border-brand rounded-2xl p-10 text-center cursor-pointer hover:border-brand-primary transition-colors"
                   data-testid="cover-upload-label">
              <ImageIcon className="mx-auto text-brand-primary" size={32} strokeWidth={1.4} />
              <p className="font-serif-display text-lg mt-2">Upload a cover image</p>
              <p className="text-sm text-brand-muted">JPG, PNG or WEBP up to 8MB</p>
              <input type="file" accept="image/*" onChange={handleCover} className="hidden" data-testid="cover-upload-input" />
            </label>
          )}
        </div>

        <div className="premium-card p-7">
          <p className="eyebrow inline-flex items-center gap-2 mb-4"><ShieldCheck size={12} strokeWidth={1.8} /> Proof documents (boosts trust score)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {proofs.map((p, i) => (
              <div key={`${p}-${i}`} className="relative aspect-square rounded-2xl overflow-hidden border border-brand bg-brand-bg flex items-center justify-center">
                {/\.(jpg|jpeg|png|webp|gif)$/i.test(p) ? (
                  <img src={p} alt={`Proof ${i+1}`} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-brand-muted px-2 text-center">PDF doc {i + 1}</span>
                )}
                <button type="button" onClick={() => setProofs((arr) => arr.filter((_, j) => j !== i))}
                        className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5"
                        data-testid={`remove-proof-${i}`}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <label className="btn-outline-gold !py-2.5 inline-flex cursor-pointer" data-testid="proof-upload-label">
            <Upload size={16} strokeWidth={1.8} className="mr-2" />
            Add proof files
            <input type="file" accept="image/*,application/pdf" multiple onChange={handleProofs} className="hidden" data-testid="proof-upload-input" />
          </label>
          {uploading && <p className="text-xs text-brand-muted mt-3">Uploading…</p>}
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-brand-muted max-w-md">By submitting, you confirm this campaign is genuine. Misuse may result in removal and a permanent ban.</p>
          <button type="submit" disabled={submitting || uploading} className="btn-gold !py-3.5 text-base" data-testid="submit-campaign-btn">
            {submitting ? "Submitting…" : "Submit for review"}
            <ArrowRight size={17} strokeWidth={1.8} className="ml-2" />
          </button>
        </div>
      </form>
    </div>
  );
}
